import { Box, Button, IconButton, styled, Tooltip, Typography } from '@mui/material'
import * as React from 'react'
import { colors } from '../../constants/colors'
import { BlueBox, HBox, SquareButton, VBox } from '../../elements/basic-elements'
import { ScrollCard } from '../../elements/cards'
import ArrowRight from '../../images/icons/arrow-right'

import { Check, Download, InfoOutlined, OpenInNew } from '@mui/icons-material'
import { graphql, Link, useStaticQuery } from 'gatsby'
import DonateIcon from '../../images/icons/donate-icon'
import FundIcon from '../../images/icons/fund-icon'
import TodoIconFilled from '../../images/icons/todo-icon-filled'
import HelpIcon from '../../images/icons/help-icon'
import InvestIcon from '../../images/icons/invest-icon'
import LearnIcon from '../../images/icons/learn-icon'
import OutreachIcon from '../../images/icons/outreach-icon'
import PurchaseIcon from '../../images/icons/purchase-icon'
import VolunteerIcon from '../../images/icons/volunteer-icon'
import { authState } from '../../state/auth-state'

import ConfirmActionCard from '../confirm-action-card'
import FullScreenModal from '../full-screen-modal'
import CenteredModal from '../modals/centered-modal'
import { ShareButtonIcon } from '../share-button'

import {
  blocksToText,
  getBeHeardLocationStringFromSanityLocation,
  getLocationType,
  getSanitySlug,
  getUniqueCobenefits,
  sanityString
} from '../../async/sanity/utils'
import { EVERY_DONATION_ACTION_SUFFIX } from '../../constants/actions'
import TodoIcon from '../../images/icons/todo-icon'
import { PillSmall } from '../../styles/styled-components/inputs/pills'
import { download, getDomain } from '../../utilities/browser'
import { extractSimpleUrl } from '../../utilities/string'
import StandardButton from '../buttons/standard-button'
import { EveryDonateButton } from '../every-donate-button'
import GetMembershipModal from '../get-membership-modal'
import ImpactScoreGlobes from '../impact-score-globes'
import TooltipTitle from '../tooltip-title'
import RepCard, { RepCardData } from './rep-card'
import Representatives from './representatives'

export type ResultCardData = {
  location: Queries.Maybe<Queries.SanityLocation>
  organization: Queries.Maybe<Queries.SanityOrganization>
  title: Queries.Maybe<string>
  rank: Queries.Maybe<number>
  description?: Queries.Maybe<readonly Queries.Maybe<Queries.SanityBlock>[]>
  tags: Queries.Maybe<readonly Queries.Maybe<Queries.SanityTag>[]>
  nouns: Queries.Maybe<readonly Queries.Maybe<Queries.SanityNoun>[]>
  materials?: readonly Queries.SanityActionMaterial[]
  slug: Queries.Maybe<Queries.SanitySlug>
  categories: Queries.Maybe<readonly Queries.Maybe<Queries.SanityCategory>[]>
  outreach: Queries.Maybe<Queries.SanityOutreach>
  donation: Queries.Maybe<Queries.SanityEveryDonation>
  actionUrl?: string
  url: Queries.Maybe<string>
  shareUrl?: string
}

export const iconsByCategory = {
  'get-educated': <LearnIcon />,
  volunteer: <VolunteerIcon />,
  purchase: <PurchaseIcon />,
  invest: <InvestIcon />,
  'get-help': <HelpIcon />,
  'get-funded': <FundIcon />,
  fundraise: <DonateIcon />,
  donate: <DonateIcon />,
  'be-heard': <OutreachIcon />
}

const ctaLabelsByCategory = {
  'be-heard': 'Learn More'
}

type ResultCardFooterType = 'list' | 'detail'

const ResultCard: React.FC<{
  data: ResultCardData
  footer: ResultCardFooterType
  consolidate?: boolean
  onDetailClick?: Function
}> = ({ data, footer, consolidate, onDetailClick }) => {
  // TODO: connect this w/ user's profile if they are logged in

  // active action

  const [confirmActionModalActive, showConfirmActionModal] = React.useState(false)

  // action representative

  const [repModalActive, showRepModal] = React.useState(false)
  const [activeRep, setActiveRep] = React.useState<RepCardData>()

  React.useEffect(() => {
    if (activeRep) {
      showRepModal(true)
    }
  }, [activeRep])

  React.useEffect(() => {
    if (!repModalActive) {
      setTimeout(() => {
        setActiveRep(undefined)
      }, 500)
    }
  }, [repModalActive])

  // user data

  const { account, actionsTaken, favorites, updateFavorite } = authState()

  // action presentation states

  const actionIsCompleted =
    actionsTaken && actionsTaken.find(({ action_slug }) => action_slug == data.slug?.current)
  const actionIsFavorited = favorites && favorites.indexOf(data.slug?.current) > -1

  // card attributes

  const orgImage = data.organization?.logo?.asset?.url
  const locationType = getLocationType(data.location)

  const locationTags = getBeHeardLocationStringFromSanityLocation(data.location)
  const firstCategory = data.categories && data.categories[0]
  const { isOutreach, script } = data.outreach || {}

  // account creation

  const uniqueCobenefits =
    data.nouns && getUniqueCobenefits(data.nouns.map(({ cobenefits }) => cobenefits).flat())
  const uniqueCobenefitsCapped = consolidate
    ? uniqueCobenefits && [...uniqueCobenefits].splice(0, 4)
    : uniqueCobenefits
  const cobenniesToShow = uniqueCobenefitsCapped

  const [createAccountModal, showCreateAccountModal] = React.useState(false)

  const { allSanityUpgradeModal } = useStaticQuery<Queries.SanityUpgradeModalQuery>(graphql`
    query SanityUpgradeModal {
      allSanityUpgradeModal {
        nodes {
          ...upgradeModal
        }
      }
    }
  `)

  // this is an annoying getaround we have to do since querying by slug
  // seems to break Gatsby
  const sanityUpgradeModal = allSanityUpgradeModal?.nodes?.find(
    ({ slug }) => slug.current === 'favorite-action-upgrade-modal'
  )
  // Components
  const TodoButton = React.useCallback(
    () => (
      <SquareButton
        variant="contained"
        color="primary"
        disableElevation
        onClick={() => {
          if (!account) {
            // show create an account warning
            showCreateAccountModal(true)
          } else {
            updateFavorite(data.slug?.current, !actionIsFavorited)
          }
        }}
      >
        {actionIsFavorited ? <TodoIconFilled /> : <TodoIcon />}
      </SquareButton>
    ),
    [account, actionIsFavorited]
  )

  const isDonateAction = firstCategory?.slug?.current == 'donate'
  const showRepresentatives = data.outreach?.isOutreach && data.outreach?.targets.length > 0

  // Show the primary CTA unless this is a representative outreach card
  const showPrimaryCta = !showRepresentatives

  return (
    // @ts-expect-error: existing type issue
    <ScrollCard elevation={0}>
      {() => ({
        content: (
          <React.Fragment>
            <VBox gap={2} alignItems="flex-start">
              <HBox gap={1} width="100%" alignItems="flex-start">
                <HBox flex={1} flexWrap="wrap" gap={1}>
                  {locationTags ? (
                    locationTags.map((tag, t) => (
                      <LocationBox key={t} type={locationType}>
                        <Typography variant="body2" color={colors.WHITE}>
                          {tag}
                        </Typography>
                      </LocationBox>
                    ))
                  ) : (
                    <LocationBox>
                      <Typography variant="body2" color={colors.WHITE}>
                        Global
                      </Typography>
                    </LocationBox>
                  )}
                </HBox>

                <HBox gap={0} flex={0} sx={{ mt: theme => theme.spacing(-0.5) }}>
                  {data.url && (
                    <Tooltip
                      placement="right"
                      title={
                        <TooltipTitle description={'Navigate to ' + extractSimpleUrl(data.url)} />
                      }
                    >
                      <IconButton
                        color="secondary"
                        onClick={() => {
                          data.url && window.open(data.url, '_blank')
                          if (!actionIsCompleted && !isOutreach) {
                            showConfirmActionModal(true)
                          }
                        }}
                      >
                        <OpenInNew />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Box sx={{ mt: theme => theme.spacing(-0.25) }}>
                    <ShareButtonIcon
                      text={data.shareUrl || getDomain() + '/results/' + data.slug?.current}
                    />
                  </Box>
                </HBox>
              </HBox>
              <Lockup gap={1}>
                {orgImage && (
                  <Box>
                    <img
                      style={{
                        maxWidth: 200,
                        maxHeight: 100,
                        objectFit: 'contain'
                      }}
                      src={orgImage}
                    />
                  </Box>
                )}
                <HBox mb={1.5} gap={1} alignItems="center">
                  <Typography color="textPrimary" variant="h3">
                    {data.title}
                  </Typography>
                </HBox>
              </Lockup>
            </VBox>
            <VBox flex={1} justifyContent="center" gap={1}>
              <Box>
                {/* @ts-expect-error: existing type issue */}
                {blocksToText(data.description).map((block, b) => (
                  <Box mb={1.2} key={b}>
                    <Typography variant="body2" color="textPrimary">
                      {block}
                    </Typography>
                  </Box>
                ))}
                {data.rank && <ImpactScoreGlobes includeLabel rank={data.rank} />}
              </Box>

              {showRepresentatives && (
                <Representatives outreach={data.outreach} onSelectRep={setActiveRep} />
              )}

              {cobenniesToShow && cobenniesToShow.length > 0 && (
                <InfoBox sx={{ my: 2 }}>
                  <Typography color="textPrimary" variant="h6">
                    Co-Benefits
                    <Tooltip
                      enterTouchDelay={0}
                      placement="top"
                      title={
                        <TooltipTitle
                          description={[
                            `Co-benefits are the intended positive side effects of a policy`
                          ]}
                        />
                      }
                    >
                      <InfoOutlined
                        fontSize="small"
                        sx={{ transform: `translateY(-0.25em)`, color: 'currentcolor' }}
                      />
                    </Tooltip>
                  </Typography>

                  <HBox flexWrap="wrap" gap={0.5}>
                    {cobenniesToShow.map((tag, t) => {
                      return (
                        <Tag key={t}>
                          <Typography variant="body2" color="textPrimary">
                            {tag?.title}
                          </Typography>
                        </Tag>
                      )
                    })}
                    {uniqueCobenefits && uniqueCobenefits.length > cobenniesToShow.length && (
                      <Button sx={{ p: 0 }} onClick={() => onDetailClick && onDetailClick()}>
                        <Tag>
                          <Typography variant="body2" color="textPrimary">
                            ...
                          </Typography>
                        </Tag>
                      </Button>
                    )}
                  </HBox>
                </InfoBox>
              )}
              {data.materials && data.materials.length > 0 && (
                <VBox>
                  <Typography color="textPrimary" variant="h6">
                    Additional Materials
                  </Typography>
                  <InfoBox sx={{ my: 2 }}>
                    {data.materials.map(
                      ({ name, description, file }: Queries.SanityActionMaterial, m) => {
                        const fileUrl = file?.asset?.url
                        const fileName = file?.asset?.originalFilename
                        return (
                          <HBox gap={2} key={m}>
                            {fileUrl && (
                              <SquareButton
                                variant="contained"
                                color={'secondary'}
                                disableElevation
                                onClick={() =>
                                  download(fileUrl, fileName ? sanityString(fileName) : undefined)
                                }
                              >
                                <Download />
                              </SquareButton>
                            )}
                            <VBox>
                              <Typography variant="body1" color="textPrimary">
                                {name}
                              </Typography>
                              <Typography mt={0.5} variant="body2" color="textPrimary">
                                {description}
                              </Typography>
                            </VBox>
                          </HBox>
                        )
                      }
                    )}
                  </InfoBox>
                </VBox>
              )}
            </VBox>

            <FullScreenModal
              open={Boolean(repModalActive)}
              colorScheme="white"
              onClose={() => {
                showRepModal(false)
                showConfirmActionModal(true)
              }}
            >
              {activeRep && (
                <RepCard
                  {...{
                    ...activeRep,
                    action: data.title,
                    script
                  }}
                />
              )}
            </FullScreenModal>
            <CenteredModal
              open={confirmActionModalActive}
              onClose={() => showConfirmActionModal(false)}
              positionAtBottomOnMobile
            >
              <ConfirmActionCard
                action={data}
                isCandidate={isOutreach}
                onDeny={() => showConfirmActionModal(false)}
              />
            </CenteredModal>

            <GetMembershipModal
              content={sanityUpgradeModal}
              open={createAccountModal}
              onClose={() => showCreateAccountModal(false)}
            />
          </React.Fragment>
        ),
        footer: (
          <Box>
            {footer === 'list' ? (
              <HBox gap={4} alignItems="flex-end">
                <HBox flexWrap="wrap" gap={0.5} flex={1}>
                  {data.tags?.map((tag, t) => {
                    return (
                      <Tag key={t}>
                        <Typography variant="body2" color="textPrimary">
                          {tag?.title}
                        </Typography>
                      </Tag>
                    )
                  })}
                </HBox>

                <HBox gap={0.5}>
                  <TodoButton />
                  <Link to={data.actionUrl}>
                    <SquareButton
                      variant="contained"
                      color={actionIsCompleted ? 'success' : 'primary'}
                      disableElevation
                      onClick={() => {
                        onDetailClick && onDetailClick()
                      }}
                    >
                      {actionIsCompleted ? (
                        <Check fontSize="large" />
                      ) : (
                        <ArrowRight color={colors.WHITE} />
                      )}
                    </SquareButton>
                  </Link>
                </HBox>
              </HBox>
            ) : (
              <HBox gap={1} width={'100%'} alignItems="flex-end">
                <VBox width="100%" gap={1}>
                  {showPrimaryCta && (
                    <StandardButton
                      variant="contained"
                      disableElevation
                      color={
                        actionIsCompleted ? 'success' : data.donation ? 'secondary' : 'primary'
                      }
                      startIcon={
                        actionIsCompleted ? (
                          <Check sx={{ width: 24, height: 24 }} />
                        ) : (
                          iconsByCategory[firstCategory?.slug?.current]
                        )
                      }
                      sx={{ color: colors.WHITE }}
                      onClick={() => {
                        data.url && window.open(data.url, '_blank')
                        if (!actionIsCompleted && !isOutreach) {
                          showConfirmActionModal(true)
                        }
                      }}
                    >
                      {isDonateAction && data.donation
                        ? 'Learn More'
                        : ctaLabelsByCategory[firstCategory?.slug?.current] || firstCategory?.title}
                    </StandardButton>
                  )}
                  {data.donation?.organization && (
                    <EveryDonateButton
                      isCompleted={
                        actionsTaken?.find(
                          ({ action_slug }) =>
                            action_slug == data.slug?.current + EVERY_DONATION_ACTION_SUFFIX
                        ) != null
                      }
                      organization={data.donation.organization}
                      action_id={getSanitySlug(data.slug)}
                    />
                  )}
                </VBox>

                <TodoButton />
              </HBox>
            )}
          </Box>
        )
      })}
    </ScrollCard>
  )
}

const locationTagColorMap = {
  federal: colors.ALZARIAN_100,
  state: colors.GEORGIAN_BAY_70,
  city: colors.GREEN_100,
  local: colors.GREEN_100
}

export const LocationBox = styled(PillSmall)<{ type?: string }>`
  ${({ theme, type }) => `
    background: ${
      type
        ? locationTagColorMap[type]
        : theme.palette.mode == 'dark'
          ? colors.DARK_MODE_50
          : colors.GEORGIAN_BAY_70
    };
  `}
`

const Tag = styled(BlueBox)``

const Lockup = styled(VBox)`
  ${({ theme }) => `
    ${theme.breakpoints.down('md')} {
      flex-direction: row;

      img {
        width: 64px;
        height: 64px;
        object-fit: contain;
      }
    }
  `}
`

export const InfoBox = styled(Box)`
  ${({ theme }) => `
    display: flex;
    padding: ${theme.spacing(2)} ${theme.spacing(2)};
    flex-direction: column;
    align-items: flex-start;
    gap: ${theme.spacing(1.25)};
    align-self: stretch;
    border-radius: ${theme.shape.borderRadius}px;
    background: ${theme.palette.mode == 'dark' ? colors.DARK_MODE_70 : colors.BLACK_10};
`}
`
export default ResultCard
